// frontend/src/components/profile/UserProfile.js
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useClerk, useUser } from '@clerk/clerk-react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Fade,
    FormControlLabel,
    Grid,
    Paper,
    Skeleton,
    TextField,
    Typography
} from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { ROLE_LABELS } from '../../routes/landing';
import { deleteMyAccount, getDeletionPreview } from '../../services/profileService';
import ConfirmDialog from '../coding/ConfirmDialog';
import SystemState from '../layout/SystemState';

const Section = ({ title, aside, tone, children }) => (
    <Paper
        component="section"
        aria-label={title}
        sx={{ p: { xs: 2.5, sm: 3 }, borderColor: tone === 'danger' ? '#F4C2C2' : 'divider' }}
    >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
            <Typography variant="h5" component="h2">{title}</Typography>
            {aside && <Typography variant="caption" color="text.secondary">{aside}</Typography>}
        </Box>
        {children}
    </Paper>
);

const Row = ({ label, children }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '120px 1fr' }, gap: { xs: 0.25, sm: 2 }, alignItems: 'center', py: 0.75 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minWidth: 0 }}>{children}</Box>
    </Box>
);

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// About you: school, state and country only, and only for teachers (wf-profile-1)
const AboutYou = () => {
    const { user, updateProfile } = useAuth();
    const saved = user?.dbProfile || {};
    const [form, setForm] = useState({ school: saved.school || '', state: saved.state || '', country: saved.country || '' });
    const [status, setStatus] = useState('idle');

    useEffect(() => {
        setForm({ school: saved.school || '', state: saved.state || '', country: saved.country || '' });
    }, [saved.school, saved.state, saved.country]);

    const change = (field) => (e) => {
        setForm((f) => ({ ...f, [field]: e.target.value }));
        if (status !== 'saving') setStatus('idle');
    };

    const save = async (e) => {
        e.preventDefault();
        setStatus('saving');
        try {
            await updateProfile(form);
            setStatus('saved');
        } catch (err) {
            setStatus('error');
        }
    };

    return (
        <Section title="About you" aside="(optional)">
            <Box component="form" onSubmit={save} noValidate>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField label="School" value={form.school} onChange={change('school')} fullWidth size="small" inputProps={{ maxLength: 120 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="State" value={form.state} onChange={change('state')} fullWidth size="small" inputProps={{ maxLength: 60 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Country" value={form.country} onChange={change('country')} fullWidth size="small" inputProps={{ maxLength: 60 }} />
                    </Grid>
                </Grid>
                <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1.5 }}>
                    Helps researchers describe the lessons you share. Subject and grade are asked for each lesson when you upload it.
                </Typography>
                {status === 'error' && (
                    <Alert severity="error" sx={{ mt: 2 }}>Couldn’t save. Try again.</Alert>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={status === 'saving'}
                        startIcon={status === 'saving' ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        Save
                    </Button>
                    <Fade in={status === 'saved'}>
                        <Typography variant="body2" role="status" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.dark' }}>
                            <CheckCircleOutline fontSize="small" /> Saved
                        </Typography>
                    </Fade>
                </Box>
            </Box>
        </Section>
    );
};

// Delete account (wf-profile 2–3). GET /api/users/profile/deletion says before the click whether the
// account may be deleted (not while it holds a slot or assignment in an open run, nor as the last
// admin) and gives the lesson counts for the dialog; DELETE answers 409 if that changed meanwhile.
const REFUSALS = {
    open_run: 'You have a place in an open run, so your account can’t be deleted yet. Ask the researcher running it to move your place, then come back.',
    last_admin: 'You are the only admin, so your account can’t be deleted. Make someone else an admin first.',
};

const DeleteAccount = () => {
    const { can, logout } = useAuth();
    const [preview, setPreview] = useState(null);
    const [previewFailed, setPreviewFailed] = useState(false);
    const [open, setOpen] = useState(false);
    const [understood, setUnderstood] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [refusal, setRefusal] = useState(null);
    const ownsLessons = can('lesson.own');

    useEffect(() => {
        let alive = true;
        getDeletionPreview()
            .then((p) => {
                if (!alive) return;
                setPreview(p);
                if (!p.allowed) setRefusal(REFUSALS[p.reason] || REFUSALS.open_run);
            })
            .catch(() => alive && setPreviewFailed(true));
        return () => {
            alive = false;
        };
    }, []);

    const start = () => {
        setOpen(true);
        setUnderstood(false);
        setError(null);
    };

    const remove = async () => {
        setBusy(true);
        setError(null);
        try {
            await deleteMyAccount();
            await logout({ redirectUrl: '/account-deleted', cause: null }); // deleting the account logged it
        } catch (err) {
            setBusy(false);
            if (err.response?.status === 409) {
                setRefusal(REFUSALS.open_run);
                setOpen(false);
            } else {
                setError('Couldn’t delete your account. Try again.');
            }
        }
    };

    if (refusal) {
        return (
            <Section title="Delete account">
                <Typography variant="body2">{refusal}</Typography>
                <Button variant="outlined" disabled sx={{ mt: 2 }}>Delete account…</Button>
            </Section>
        );
    }

    const unshared = preview?.unsharedLessons ?? 0;
    const shared = preview?.sharedLessons ?? 0;

    return (
        <Section title="Delete account" tone="danger">
            <Typography variant="body2">
                {ownsLessons
                    ? 'Deletes your account and the lessons you haven’t shared, with their analyses. Lessons you shared for research stay with the research team.'
                    : 'Deletes your account and signs you out.'}
            </Typography>
            <Button variant="outlined" color="error" onClick={start} disabled={!preview && !previewFailed} sx={{ mt: 2 }}>
                Delete account…
            </Button>

            <ConfirmDialog
                open={open}
                title="Delete your account?"
                confirmLabel="Delete account"
                destructive
                busy={busy}
                error={error}
                confirmDisabled={!understood}
                onConfirm={remove}
                onClose={() => setOpen(false)}
            >
                <Box component="ul" sx={{ pl: 2.5, m: 0, color: 'text.primary', '& li': { mb: 1, typography: 'body2' } }}>
                    {preview && unshared > 0 && (
                        <li>{plural(unshared, 'lesson', 'lessons')} you haven’t shared {unshared === 1 ? 'is' : 'are'} deleted, with {unshared === 1 ? 'its analysis' : 'their analyses'}.</li>
                    )}
                    {preview && shared > 0 && (
                        <li>{plural(shared, 'lesson', 'lessons')} you shared {shared === 1 ? 'stays' : 'stay'} with the research team. {shared === 1 ? 'It is' : 'They are'} no longer linked to your account.</li>
                    )}
                    {ownsLessons && previewFailed && (
                        <li>Lessons you haven’t shared are deleted, with their analyses. Lessons you shared stay with the research team.</li>
                    )}
                    <li>You are signed out. This email can sign up again later, as a new account.</li>
                </Box>
                <FormControlLabel
                    sx={{ mt: 1, color: 'text.primary' }}
                    control={<Checkbox checked={understood} onChange={(e) => setUnderstood(e.target.checked)} disabled={busy} />}
                    label={<Typography variant="body2">I understand this can’t be undone</Typography>}
                />
            </ConfirmDialog>
        </Section>
    );
};

// /profile for every role (profile.own, wf-profile 1–3): the account from the sign-in service,
// About you for teachers only, and deleting one's own account.
const UserProfile = () => {
    const { user: clerkUser, isLoaded } = useUser();
    const { openUserProfile } = useClerk();
    const { user } = useAuth();
    const isTeacher = user?.role === 'user';

    return (
        <Box sx={{ maxWidth: 760, mx: 'auto', display: 'grid', gap: 2.5 }}>
            <Typography variant="h2" component="h1" sx={{ mt: { xs: 1, sm: 2 } }}>Profile</Typography>

            <Section title="Account">
                <Row label="Name">
                    {isLoaded ? <Typography variant="body2">{clerkUser?.fullName || '—'}</Typography> : <Skeleton width={160} />}
                </Row>
                <Row label="Email">
                    {isLoaded
                        ? <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{clerkUser?.primaryEmailAddress?.emailAddress}</Typography>
                        : <Skeleton width={200} />}
                </Row>
                <Row label="Role">
                    <Chip size="small" label={ROLE_LABELS[user?.role] || user?.role || '—'} sx={{ borderRadius: 999, fontWeight: 600 }} />
                    <Typography variant="caption" color="text.secondary">Roles are changed by an admin.</Typography>
                </Row>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1.5 }}>
                    <Button variant="outlined" onClick={() => openUserProfile()}>Manage email and password</Button>
                    <Typography variant="caption" color="text.secondary">Opens the sign-in service’s account page</Typography>
                </Box>
            </Section>

            {isTeacher && <AboutYou />}
            <DeleteAccount />
        </Box>
    );
};

// wf-profile-4: shown once after a delete, logo only (the session is gone)
export const AccountDeleted = () => (
    <SystemState
        title="Your account has been deleted"
        actions={<Button variant="contained" component={RouterLink} to="/">Go to the ADAS home page</Button>}
    >
        You have been signed out. Lessons you shared stay with the research team.
    </SystemState>
);

export default UserProfile;
